import React from "react";

/* Academy Visuals — reusable inline SVG clinical illustrations
   -------------------------------------------------------------
   Each illustration is a small, teal-tinted SVG that plugs into the
   `topic-teach` slide as the visual card. They use CSS variables from
   the app palette (primary teal, muted foreground) so they blend
   seamlessly into any card background.

   Design guidelines followed here:
   - Native palette only. No purple, no bright reds, no aqua.
   - Line style + subtle fills. Medical-illustration feel, not clip-art.
   - Text labels use font-display via className, small + all-caps.

   Add new illustrations by exporting a React component and registering
   it in VISUALS_BY_KIND at the bottom of this file.
*/


/* ─── shared style tokens ─── */

const STROKE = "hsl(var(--primary))";
const STROKE_MUTED = "hsl(var(--muted-foreground))";
const FILL_TINT = "hsl(var(--primary) / 0.08)";
const FILL_STRONG = "hsl(var(--primary) / 0.18)";
const LABEL_CLS = "text-[10px] font-bold uppercase tracking-[0.1em] fill-current";


/* ─── 1. Angle classification (Class I / II / III molars) ─── */

export function AngleClassificationSVG({ className = "" }) {
    // Three mini-diagrams side by side: upper (top) + lower (bottom) molar
    // circles, arranged in Class I (aligned), Class II (upper ahead of lower),
    // Class III (lower ahead of upper).
    const items = [
        { label: "Class I", upperOffset: 0 },
        { label: "Class II", upperOffset: -10 },
        { label: "Class III", upperOffset: 10 },
    ];
    return (
        <svg viewBox="0 0 260 130" className={`w-full max-w-[360px] ${className}`} role="img" aria-label="Angle classification: Class I, II, III">
            {items.map((item, i) => (
                <g key={item.label} transform={`translate(${i * 90}, 20)`}>
                    {/* Upper molar */}
                    <circle cx={30 + item.upperOffset} cy={30} r={16} fill={FILL_STRONG} stroke={STROKE} strokeWidth={1.5} />
                    {/* Lower molar */}
                    <circle cx={30} cy={70} r={16} fill="white" stroke={STROKE} strokeWidth={1.5} />
                    {/* Groove marker on lower */}
                    <line x1={30} y1={54} x2={30} y2={62} stroke={STROKE_MUTED} strokeWidth={1.2} />
                    {/* Cusp indicator on upper */}
                    <line x1={30 + item.upperOffset} y1={46} x2={30 + item.upperOffset} y2={38} stroke={STROKE_MUTED} strokeWidth={1.2} />
                    {/* Label */}
                    <text x={30} y={110} textAnchor="middle" className={LABEL_CLS} style={{ color: "hsl(var(--foreground))" }}>{item.label}</text>
                </g>
            ))}
        </svg>
    );
}


/* ─── 2. Dental vs skeletal profile silhouettes ─── */

export function DentalVsSkeletalSVG({ className = "" }) {
    // Two side profiles: DENTAL (teeth only pushed forward) and SKELETAL (jaw retruded)
    const profiles = [
        {
            label: "Dental",
            path: "M 50 10 Q 78 10 82 40 L 82 70 Q 78 85 62 85 L 40 85 Q 24 82 24 60 L 24 30 Q 26 12 50 10 Z",
        },
        {
            label: "Skeletal",
            path: "M 50 10 Q 78 10 82 40 L 82 65 Q 78 78 55 82 L 40 85 Q 24 82 24 60 L 24 30 Q 26 12 50 10 Z",
        },
    ];
    return (
        <svg viewBox="0 0 260 130" className={`w-full max-w-[360px] ${className}`} role="img" aria-label="Dental vs skeletal discrepancy">
            {profiles.map((p, i) => (
                <g key={p.label} transform={`translate(${i * 140}, 20)`}>
                    <path d={p.path} fill={FILL_TINT} stroke={STROKE} strokeWidth={1.5} />
                    {/* Teeth marker — flared forward */}
                    <line x1={80} y1={55} x2={90} y2={53} stroke={STROKE} strokeWidth={2} />
                    <line x1={80} y1={58} x2={90} y2={60} stroke={STROKE} strokeWidth={2} />
                    <text x={54} y={110} textAnchor="middle" className={LABEL_CLS} style={{ color: "hsl(var(--foreground))" }}>{p.label}</text>
                </g>
            ))}
        </svg>
    );
}


/* ─── 3. CVM growth stages (6 vertebra shapes) ─── */

export function CVMStagesSVG({ className = "" }) {
    // Six stylised C-vertebra shapes progressing from flat inferior border
    // (CVM 1) through concave (CVM 3 = peak) to rectangular (CVM 6).
    const stages = [
        // CVM 1 — flat inferior
        "M 8 8 L 32 8 L 32 22 L 8 22 Z",
        // CVM 2 — slight notch
        "M 8 8 L 32 8 L 32 22 Q 20 18 8 22 Z",
        // CVM 3 — clearer concavity (peak)
        "M 8 8 L 32 8 L 32 24 Q 20 12 8 24 Z",
        // CVM 4 — deeper concavity + trapezoid
        "M 8 6 L 32 8 L 32 24 Q 20 14 8 24 Z",
        // CVM 5 — square with concavity
        "M 6 6 L 34 6 L 34 24 Q 20 16 6 24 Z",
        // CVM 6 — rectangular
        "M 6 6 L 34 6 L 34 26 L 6 26 Z",
    ];
    return (
        <svg viewBox="0 0 280 60" className={`w-full max-w-[380px] ${className}`} role="img" aria-label="Cervical vertebral maturation stages 1 to 6">
            {stages.map((d, i) => {
                const isPeak = i === 2 || i === 3;
                return (
                    <g key={i} transform={`translate(${i * 46}, 4)`}>
                        <path d={d} fill={isPeak ? FILL_STRONG : FILL_TINT} stroke={STROKE} strokeWidth={1.4} />
                        <text x={20} y={44} textAnchor="middle" className={LABEL_CLS} style={{ color: "hsl(var(--foreground))" }}>{`CVM ${i + 1}`}</text>
                        {isPeak && (
                            <text x={20} y={54} textAnchor="middle" style={{ fontSize: "7px", fill: "hsl(var(--primary))", fontWeight: 700 }}>PEAK</text>
                        )}
                    </g>
                );
            })}
        </svg>
    );
}


/* ─── 4. PDL pressure-tension diagram ─── */

export function PDLForcesSVG({ className = "" }) {
    // A single tooth with a horizontal force arrow, showing pressure side
    // (compressed PDL, osteoclasts) and tension side (stretched PDL, osteoblasts)
    return (
        <svg viewBox="0 0 300 160" className={`w-full max-w-[380px] ${className}`} role="img" aria-label="PDL pressure and tension zones">
            {/* Tooth outline */}
            <path
                d="M 130 30 Q 120 30 118 45 L 118 90 Q 118 100 130 108 L 170 108 Q 182 100 182 90 L 182 45 Q 180 30 170 30 Z"
                fill="white" stroke={STROKE} strokeWidth={1.5}
            />
            {/* Root */}
            <path
                d="M 130 108 L 138 145 L 162 145 L 170 108 Z"
                fill="white" stroke={STROKE} strokeWidth={1.5}
            />
            {/* Pressure side PDL (right) — filled darker */}
            <path
                d="M 170 32 L 178 30 L 180 108 L 172 108 Z M 170 108 L 178 108 L 170 145 L 162 145 Z"
                fill={FILL_STRONG} stroke="none"
            />
            {/* Tension side PDL (left) — dashed */}
            <path
                d="M 118 32 L 110 30 L 108 108 L 116 108 Z M 118 108 L 110 108 L 130 145 L 138 145 Z"
                fill={FILL_TINT} stroke={STROKE} strokeWidth={0.8} strokeDasharray="2 2"
            />
            {/* Force arrow at crown, pushing right */}
            <line x1={80} y1={60} x2={115} y2={60} stroke={STROKE} strokeWidth={2.5} />
            <polygon points="115,60 108,56 108,64" fill={STROKE} />
            {/* Labels */}
            <text x={65} y={53} className={LABEL_CLS} style={{ color: "hsl(var(--primary))" }}>Force</text>
            <text x={225} y={85} className={LABEL_CLS} style={{ color: "hsl(var(--foreground))" }}>Pressure</text>
            <text x={225} y={98} style={{ fontSize: "8px", fill: STROKE_MUTED }}>(bone resorbs)</text>
            <text x={12} y={85} className={LABEL_CLS} style={{ color: "hsl(var(--foreground))" }}>Tension</text>
            <text x={12} y={98} style={{ fontSize: "8px", fill: STROKE_MUTED }}>(bone forms)</text>
        </svg>
    );
}


/* ─── 5. Force couple diagram ─── */

export function ForceCoupleSVG({ className = "" }) {
    // Two side-by-side tooth diagrams: single force (tipping) vs couple (translation)
    const teeth = [{ isCouple: false }, { isCouple: true }];
    return (
        <svg viewBox="0 0 300 180" className={`w-full max-w-[380px] ${className}`} role="img" aria-label="Force vs couple biomechanics">
            {teeth.map((t, i) => (
                <g key={t.isCouple ? "couple" : "force"} transform={`translate(${i * 160}, 15)`}>
                    {/* Tooth */}
                    <path d="M 40 25 Q 30 25 28 40 L 28 80 L 52 80 L 52 40 Q 50 25 40 25 Z" fill="white" stroke={STROKE} strokeWidth={1.5} />
                    {/* Root */}
                    <path d="M 28 80 L 34 115 L 46 115 L 52 80 Z" fill="white" stroke={STROKE} strokeWidth={1.5} />
                    {/* CR (centre of resistance) — dot on root */}
                    <circle cx={40} cy={95} r={2.5} fill={STROKE} />
                    <text x={40} y={135} textAnchor="middle" style={{ fontSize: "7px", fill: STROKE_MUTED }}>CR</text>
                    {/* Force arrows */}
                    {!t.isCouple && (
                        <>
                            <line x1={6} y1={40} x2={26} y2={40} stroke={STROKE} strokeWidth={2.5} />
                            <polygon points="26,40 20,37 20,43" fill={STROKE} />
                            {/* Rotation curve suggesting tipping */}
                            <path d="M 60 90 Q 68 78 60 66" stroke={STROKE} strokeWidth={1.2} fill="none" strokeDasharray="2 2" />
                            <polygon points="60,66 63,72 57,72" fill={STROKE} />
                        </>
                    )}
                    {t.isCouple && (
                        <>
                            {/* Top force pushing right */}
                            <line x1={6} y1={40} x2={26} y2={40} stroke={STROKE} strokeWidth={2.5} />
                            <polygon points="26,40 20,37 20,43" fill={STROKE} />
                            {/* Bottom force pushing left */}
                            <line x1={72} y1={80} x2={52} y2={80} stroke={STROKE} strokeWidth={2.5} />
                            <polygon points="52,80 58,77 58,83" fill={STROKE} />
                            {/* Translation arrow */}
                            <line x1={40} y1={20} x2={64} y2={20} stroke={STROKE_MUTED} strokeWidth={1.2} strokeDasharray="2 2" />
                            <polygon points="64,20 58,17 58,23" fill={STROKE_MUTED} />
                        </>
                    )}
                    <text x={40} y={155} textAnchor="middle" className={LABEL_CLS} style={{ color: "hsl(var(--foreground))" }}>
                        {t.isCouple ? "Couple → translation" : "Force → tipping"}
                    </text>
                </g>
            ))}
        </svg>
    );
}


/* ─── 6. Attachment on tooth ─── */

export function AttachmentToothSVG({ className = "" }) {
    return (
        <svg viewBox="0 0 260 160" className={`w-full max-w-[360px] ${className}`} role="img" aria-label="Composite attachment bonded to tooth surface">
            {/* Tooth crown */}
            <path d="M 90 30 Q 78 30 76 55 L 76 110 L 138 110 L 138 55 Q 136 30 108 30 Z" fill="white" stroke={STROKE} strokeWidth={1.5} />
            {/* Root */}
            <path d="M 76 110 L 84 145 L 130 145 L 138 110 Z" fill="white" stroke={STROKE} strokeWidth={1.5} />
            {/* Attachment (small rectangle bonded to buccal) */}
            <rect x={100} y={72} width={14} height={20} rx={2} fill={FILL_STRONG} stroke={STROKE} strokeWidth={1.5} />
            {/* Aligner (dashed outline hugging the crown) */}
            <path d="M 68 32 L 68 116 L 146 116 L 146 32" fill="none" stroke={STROKE} strokeWidth={1.2} strokeDasharray="4 3" />
            {/* Callout arrow */}
            <line x1={175} y1={82} x2={120} y2={82} stroke={STROKE_MUTED} strokeWidth={1} />
            <text x={180} y={80} className={LABEL_CLS} style={{ color: "hsl(var(--primary))" }}>Attachment</text>
            <text x={180} y={94} style={{ fontSize: "8px", fill: STROKE_MUTED }}>engages aligner</text>
            {/* Bottom label */}
            <text x={107} y={30} textAnchor="middle" style={{ fontSize: "8px", fill: STROKE_MUTED }}>Aligner tray</text>
        </svg>
    );
}


/* ─── 7. IPR strip ─── */

export function IPRStripSVG({ className = "" }) {
    return (
        <svg viewBox="0 0 260 150" className={`w-full max-w-[360px] ${className}`} role="img" aria-label="Interproximal reduction between two teeth">
            {/* Left tooth */}
            <path d="M 60 40 Q 50 40 48 60 L 48 120 L 100 120 L 100 60 Q 98 40 82 40 Z" fill="white" stroke={STROKE} strokeWidth={1.5} />
            {/* Right tooth */}
            <path d="M 160 40 Q 150 40 148 60 L 148 120 L 200 120 L 200 60 Q 198 40 182 40 Z" fill="white" stroke={STROKE} strokeWidth={1.5} />
            {/* IPR strip (dashed) between teeth */}
            <line x1={124} y1={45} x2={124} y2={120} stroke={STROKE} strokeWidth={2} strokeDasharray="3 2" />
            {/* Bracket labels */}
            <line x1={100} y1={135} x2={148} y2={135} stroke={STROKE_MUTED} strokeWidth={1} />
            <line x1={100} y1={130} x2={100} y2={140} stroke={STROKE_MUTED} strokeWidth={1} />
            <line x1={148} y1={130} x2={148} y2={140} stroke={STROKE_MUTED} strokeWidth={1} />
            <text x={124} y={30} textAnchor="middle" className={LABEL_CLS} style={{ color: "hsl(var(--primary))" }}>IPR</text>
            <text x={124} y={148} textAnchor="middle" style={{ fontSize: "8px", fill: STROKE_MUTED }}>0.3 – 0.5 mm per contact</text>
        </svg>
    );
}


/* ─── 8. Aligner arch cross-section ─── */

export function AlignerArchSVG({ className = "" }) {
    return (
        <svg viewBox="0 0 300 150" className={`w-full max-w-[380px] ${className}`} role="img" aria-label="Clear aligner cross-section over dental arch">
            {/* Arch of 7 teeth */}
            {[0, 1, 2, 3, 4, 5, 6].map((i) => {
                const angle = -Math.PI / 2 - Math.PI / 2 + (Math.PI / 6) * i;
                const cx = 150 + 90 * Math.cos(angle);
                const cy = 100 + 90 * Math.sin(angle);
                return (
                    <rect key={i} x={cx - 10} y={cy - 15} width={20} height={30} rx={3} fill="white" stroke={STROKE} strokeWidth={1.3}
                        transform={`rotate(${(angle * 180 / Math.PI) + 90}, ${cx}, ${cy})`}
                    />
                );
            })}
            {/* Aligner overlay — dashed arc hugging the outside of the arch */}
            <path d="M 60 105 Q 60 25 150 22 Q 240 25 240 105" fill="none" stroke={STROKE} strokeWidth={1.8} strokeDasharray="5 3" />
            <text x={150} y={140} textAnchor="middle" className={LABEL_CLS} style={{ color: "hsl(var(--primary))" }}>Aligner over arch</text>
        </svg>
    );
}


/* ─── 9. Predictability bar chart ─── */

export function PredictabilityBarsSVG({ className = "" }) {
    const movements = [
        { label: "Crowding", pct: 88 },
        { label: "Rotation (incisor)", pct: 82 },
        { label: "Spacing", pct: 78 },
        { label: "Extrusion (incisor)", pct: 58 },
        { label: "Torque (canine)", pct: 45 },
        { label: "Distalisation (molar)", pct: 30 },
    ];
    return (
        <svg viewBox="0 0 300 180" className={`w-full max-w-[380px] ${className}`} role="img" aria-label="Aligner movement predictability chart">
            {movements.map((m, i) => {
                const y = 12 + i * 26;
                const w = m.pct * 1.6;
                const good = m.pct >= 75;
                const mid = m.pct >= 50 && m.pct < 75;
                return (
                    <g key={i}>
                        <text x={0} y={y + 8} style={{ fontSize: "9px", fill: "hsl(var(--foreground))", fontWeight: 500 }}>{m.label}</text>
                        <rect x={140} y={y} width={160} height={12} fill={FILL_TINT} rx={2} />
                        <rect x={140} y={y} width={w} height={12} rx={2}
                            fill={good ? "hsl(var(--primary))" : mid ? "#eab308" : "#f97316"}
                        />
                        <text x={140 + w + 5} y={y + 10} style={{ fontSize: "9px", fill: STROKE_MUTED, fontWeight: 700 }}>{m.pct}%</text>
                    </g>
                );
            })}
        </svg>
    );
}


/* ─── 10. Arch scan pass sequence ─── */

export function ArchScanSequenceSVG({ className = "" }) {
    return (
        <svg viewBox="0 0 300 180" className={`w-full max-w-[380px] ${className}`} role="img" aria-label="Intraoral scan pass sequence">
            {/* U-shaped arch (top-down view) */}
            <path
                d="M 40 40 L 40 120 Q 40 150 90 150 L 210 150 Q 260 150 260 120 L 260 40"
                fill="none" stroke={STROKE} strokeWidth={2.5}
            />
            {/* Occlusal pass label */}
            <text x={150} y={30} textAnchor="middle" className={LABEL_CLS} style={{ color: "hsl(var(--primary))" }}>1 · Occlusal</text>
            {/* Occlusal arrow along the top of arch */}
            <line x1={45} y1={45} x2={255} y2={45} stroke={STROKE} strokeWidth={1.4} strokeDasharray="4 2" />
            <polygon points="255,45 248,41 248,49" fill={STROKE} />
            {/* Buccal pass — outside arch */}
            <text x={150} y={175} textAnchor="middle" style={{ fontSize: "8px", fill: STROKE_MUTED }}>3 · Lingual (inside)</text>
            <text x={20} y={80} style={{ fontSize: "8px", fill: STROKE_MUTED, fontWeight: 700 }}>2 · Buccal</text>
            {/* Buccal arrow — curving along the outer arch */}
            <path d="M 25 60 L 25 125 Q 25 165 90 165 L 210 165 Q 275 165 275 125 L 275 60"
                fill="none" stroke={STROKE_MUTED} strokeWidth={1.2} strokeDasharray="3 2" />
            {/* Lingual arrow — inside curve */}
            <path d="M 60 70 L 60 115 Q 60 140 100 140 L 200 140 Q 240 140 240 115 L 240 70"
                fill="none" stroke={STROKE_MUTED} strokeWidth={1.2} strokeDasharray="3 2" />
        </svg>
    );
}


/* ─── 11. Ceph landmarks (simplified) ─── */

export function CephLandmarksSVG({ className = "" }) {
    return (
        <svg viewBox="0 0 260 180" className={`w-full max-w-[360px] ${className}`} role="img" aria-label="Cephalometric landmarks">
            {/* Head profile */}
            <path
                d="M 80 30 Q 130 20 155 50 L 165 90 L 175 105 L 170 115 L 175 125 L 165 140 Q 155 145 145 150 L 100 155 Q 70 155 55 130 L 50 90 Q 50 45 80 30 Z"
                fill={FILL_TINT} stroke={STROKE} strokeWidth={1.5}
            />
            {/* Landmark dots */}
            {[
                { x: 130, y: 40, l: "S" },
                { x: 155, y: 50, l: "N" },
                { x: 165, y: 105, l: "A" },
                { x: 168, y: 132, l: "B" },
                { x: 165, y: 145, l: "Pog" },
            ].map((p, i) => (
                <g key={i}>
                    <circle cx={p.x} cy={p.y} r={3} fill={STROKE} />
                    <text x={p.x + 6} y={p.y + 3} style={{ fontSize: "9px", fill: STROKE, fontWeight: 700 }}>{p.l}</text>
                </g>
            ))}
            {/* SN line */}
            <line x1={130} y1={40} x2={155} y2={50} stroke={STROKE} strokeWidth={1} strokeDasharray="3 2" />
            {/* NA line */}
            <line x1={155} y1={50} x2={165} y2={105} stroke={STROKE} strokeWidth={1} strokeDasharray="3 2" />
            {/* NB line */}
            <line x1={155} y1={50} x2={168} y2={132} stroke={STROKE} strokeWidth={1} strokeDasharray="3 2" />
            <text x={195} y={95} className={LABEL_CLS} style={{ color: "hsl(var(--primary))" }}>Ceph</text>
            <text x={195} y={107} style={{ fontSize: "8px", fill: STROKE_MUTED }}>S · N · A · B · Pog</text>
        </svg>
    );
}


/* ─── 12. Andrews' Six Keys checklist ─── */

export function AndrewsKeysSVG({ className = "" }) {
    const keys = [
        "Molar relationship",
        "Crown angulation (tip)",
        "Crown inclination (torque)",
        "No rotations",
        "Tight contacts",
        "Flat Curve of Spee",
    ];
    return (
        <svg viewBox="0 0 300 180" className={`w-full max-w-[380px] ${className}`} role="img" aria-label="Andrews' Six Keys of ideal occlusion">
            {keys.map((k, i) => {
                const col = i % 2;
                const row = Math.floor(i / 2);
                const x = 10 + col * 150;
                const y = 20 + row * 45;
                return (
                    <g key={i}>
                        <rect x={x} y={y} width={140} height={35} rx={6} fill={FILL_TINT} stroke={STROKE} strokeWidth={1.2} />
                        <circle cx={x + 18} cy={y + 17} r={9} fill={STROKE} />
                        <text x={x + 18} y={y + 20} textAnchor="middle" style={{ fontSize: "10px", fill: "white", fontWeight: 700 }}>{i + 1}</text>
                        <text x={x + 34} y={y + 21} style={{ fontSize: "9px", fill: "hsl(var(--foreground))", fontWeight: 600 }}>{k}</text>
                    </g>
                );
            })}
        </svg>
    );
}


/* ─── Registry: visual_kind → component ─── */

export const VISUALS_BY_KIND = {
    angle_class:         AngleClassificationSVG,
    dental_vs_skeletal:  DentalVsSkeletalSVG,
    cvm_stages:          CVMStagesSVG,
    pdl_forces:          PDLForcesSVG,
    force_couple:        ForceCoupleSVG,
    attachment_tooth:    AttachmentToothSVG,
    ipr_strip:           IPRStripSVG,
    aligner_arch:        AlignerArchSVG,
    predictability_bars: PredictabilityBarsSVG,
    arch_scan_sequence:  ArchScanSequenceSVG,
    ceph_landmarks:      CephLandmarksSVG,
    andrews_keys:        AndrewsKeysSVG,
};

export function AcademyVisual({ kind, className = "" }) {
    const Component = VISUALS_BY_KIND[kind];
    if (!Component) return null;
    return <Component className={className} />;
}
